/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * TypeScript type definitions for the EOS Transfer Media flow.
 * Migrated from: lego/page/eos/transfer-media/
 */

import type { EosTemplateMapDTO } from '../../../services/api/api.eos'

// ---------------------------------------------------------------------------
// FTP Context (study configuration for transfer)
// ---------------------------------------------------------------------------

export interface FtpContextMetadata {
  user: string
  password: string
}

export interface FtpContext {
  id: number | null
  host: string
  name: string
  port: number
  implicit: boolean
  remoteFolder: string
  remoteFolderName: string
  metadata: FtpContextMetadata
  enabled: boolean
  frequency: number
  targetFolder: number | string
  targetFolderName: string
  targetFolderPath?: string
  eosMediaTransfer: boolean
  archived?: boolean
  studyFolderId?: number
  mccEnabled?: boolean
  contentType?: string
  dataType?: string
  legacyStudy?: boolean
  eosStudyGroupUuid?: string
  eosStudyUuid?: string
  eosStudyEnvUuid?: string | null
  comment?: string
  studyTransferInProgress?: boolean
}

// ---------------------------------------------------------------------------
// Study Config (study group / study / environment dropdowns)
// ---------------------------------------------------------------------------

export interface StudyGroupOption {
  uuid: string
  name: string
  mccEnabled?: boolean
}

export interface StudyOption {
  uuid: string
  name: string
}

export interface EnvironmentOption {
  uuid: string
  name: string
}

export interface StudyConfig {
  studyGroup: StudyGroupOption[] | null
  study: StudyOption[] | null
  environment: EnvironmentOption[] | null
}

// ---------------------------------------------------------------------------
// Select option (for react-select dropdowns)
// ---------------------------------------------------------------------------

export interface SelectOption {
  label: string
  value: string
}

// ---------------------------------------------------------------------------
// Destination folder (folder picker selection)
// ---------------------------------------------------------------------------

export interface DestinationFolder {
  id: number
  name: string
}

// ---------------------------------------------------------------------------
// Table header definition
// ---------------------------------------------------------------------------

export interface TableHeader {
  column: string
  text: string
  className?: string
}

// ---------------------------------------------------------------------------
// Sort state
// ---------------------------------------------------------------------------

export interface SortState {
  descending: boolean
  column: string
}

// ---------------------------------------------------------------------------
// Study states filter
// ---------------------------------------------------------------------------

export type StudyStateFilter = 'ACTIVE' | 'ARCHIVED' | 'ALL'

export const STUDY_STATES: Record<string, StudyStateFilter> = {
  active: 'ACTIVE',
  archived: 'ARCHIVED',
  all: 'ALL'
} as const

// ---------------------------------------------------------------------------
// Acknowledgement document selection
// ---------------------------------------------------------------------------

export interface AckDocTemplate {
  id: number
  name: string
}

export interface AckDocTemplateListResponse {
  ackDocTemplates: AckDocTemplate[]
  siteAckDocTemplateId: number
  sponsorAckDocTemplateId: number
}

export interface SelectedDocTypes {
  eosStudyId: number
  siteAckTemplateFsiId: number
  siteAckTemplateFsiName: string
  sponsorAckTemplateFsiId: number
  sponsorAckTemplateFsiName: string
}

// ---------------------------------------------------------------------------
// Component Props
// ---------------------------------------------------------------------------

export interface EosTransferMediaExistingFtpsModalProps {
  isPdrFlow?: boolean
}

export interface TableMenuProps {
  item: FtpContext
  isPdfgenStudy: boolean
  userRole: string | null
  isPdrFlow?: boolean
  reTransfer: (id: number) => void
}

export interface EosStudyInfoDialogProps {
  ftpId: number
  preventAutoTransferMedia?: boolean
  isReview?: boolean
}

export interface PdfGenStudyDetailsProps {
  ftpId: number
  isReview?: boolean
  isPdrFlow?: boolean
}

export interface ArchiveStudyModalProps {
  ftpConfig: FtpContext
}

export interface ConfirmRemoveStudyProps {
  ftpConfig: FtpContext
  isPdrFlow?: boolean
}

export interface ConfirmUpdateEosInfoProps {
  ftpConfig: FtpContext
  isPdrFlow?: boolean
}

export interface EosTransferMediaStudyStatusFilterProps {
  filterList: (selection: StudyStateFilter) => void
}

export interface AcknowledgementDocSelectionProps {
  handleAckDocUpdate: (data: SelectedDocTypes) => void
  contextId: number | undefined
  isLegacyStudy?: boolean
  isReview?: boolean
}

// ---------------------------------------------------------------------------
// FTP Create request (for updateFtpContext)
// ---------------------------------------------------------------------------

export interface FtpCreateRequest {
  eosTemplateMapDTO: EosTemplateMapDTO
  ftpContextRequestDTO: FtpContext
  id?: number | null
}

// ---------------------------------------------------------------------------
// Study info response (from loadStudyInfo)
// ---------------------------------------------------------------------------

export interface StudyInfoResponse {
  studyGroup: string
  study: string
  environment: string | null
  mccEnabled?: boolean
}

// ---------------------------------------------------------------------------
// Formik form values
// ---------------------------------------------------------------------------

export interface FtpEditFormValues {
  data: {
    studyName: string
    hostAddress: string
    port: string
    transferMode: 'IMPLICIT' | 'EXPLICIT'
    studyLevelPath: string
    username: string
    password: string
    destinationFolder: string
    studyGroup: SelectOption | null
    study: SelectOption | null
    environment: SelectOption | null
  }
}

export interface StudyInfoFormValues {
  data: {
    password: string
    port: string
    studyFolderPath: string
    transferMode: 'IMPLICIT' | 'EXPLICIT'
    username: string
  }
}
